import Graphic from '@arcgis/core/Graphic';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Feature from '@arcgis/core/widgets/Feature.js';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import React, { useEffect, useState } from 'react';

import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import { useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  AnyLayer,
  FeatureDetailsState,
  QueryableLayer
} from './useFeatureDetails';
import { takeOnlyCompleted, useWebMap } from './useFeatureDetails';

export type GridElementRelatedFeatureLayer2 =
  | {
      type: 'NOT_LOADED';
    }
  | { type: 'LOADING' }
  | {
      type: 'LOADED';
      layer: Record<DomainGuid, __esri.FeatureLayer>;
    }
  | {
      type: 'NOT_AVAILABLE';
      reason: string;
    };

const useFeatureLayerFromWebMapForFeatures = (
  featuresWithIds: FeatureWithIDs<unknown>[] | undefined
) => {
  const [featureLayer, setFeatureLayer] =
    useState<GridElementRelatedFeatureLayer2>({ type: 'NOT_LOADED' });

  const [allAvailableFeatureLayersLoaded, setAllAvailableFeaturesLoaded] =
    useState(false);

  const webMap = useWebMap();

  React.useEffect(() => {
    if (
      featureLayer.type === 'LOADED' ||
      allAvailableFeatureLayersLoaded === true ||
      webMap.type !== 'READY' ||
      !featuresWithIds
    )
      return;
    queryGridElementsDetailsByGlobalID2(
      webMap.webMap.allLayers
        .filter(layer => (layer as any).layerId != null)
        .map(layer => ({
          globalIds: featuresWithIds
            .filter(f => f.gc.layerId === (layer as any).layerId)
            .map(f => f.gc.globalId),
          layer
        }))
        .filter(f => f.globalIds.length > 0)
        .toArray()
    ).then(matchingFeatures => {
      setAllAvailableFeaturesLoaded(true);
      const matchingLayers = matchingFeatures.filter(
        featureSet => featureSet.features.length > 0
      );

      if (matchingLayers.length === 0) {
        setFeatureLayer({
          type: 'NOT_AVAILABLE',
          reason: 'No matching layers found. Please check webmap configuration.'
        });
      }

      const featureLayer: GridElementRelatedFeatureLayer2 = {
        type: 'LOADED',
        layer: matchingLayers
          .flatMap(set => set.features)
          // ELVIA DEMO - FIX AFTER!!!!!!!!!!!!
          .filter(
            feature =>
              feature.attributes.globalid || feature.attributes.GLOBALID
          )
          .reduce((acc, feature) => {
            // ELVIA DEMO - FIX AFTER!!!!!!!!!!!!
            const globalId = (feature.attributes.globalid ||
              feature.attributes.GLOBALID) as DomainGuid;
            const layer = feature.layer as __esri.FeatureLayer;
            acc[globalId] = layer;
            return acc;
          }, {} as Record<DomainGuid, __esri.FeatureLayer>)
      };

      setFeatureLayer(featureLayer);
    });
  }, [featureLayer, webMap]);

  React.useEffect(() => {
    setFeatureLayer({ type: 'LOADING' });
  }, [webMap]);

  React.useEffect(() => {
    setFeatureLayer({ type: 'NOT_LOADED' });
    setAllAvailableFeaturesLoaded(false);
  }, [featuresWithIds]);

  return featureLayer;
};

type TitleAndObjectId = {
  globalId: DomainGuid;
  title: string;
};

export function useTitlesOfFeatures({
  of: featuresWithIds
}: {
  of: FeatureWithIDs<unknown>[] | undefined;
}) {
  const [titles, setTitle] = useState<TitleAndObjectId[]>([]);
  const [featureDetailsLoadingStatus, setFeatureDetailsLoadingStatus] =
    React.useState<FeatureDetailsState>('NOT_LOADED');
  const [featureWithId, selectFeature] = React.useState<
    FeatureWithIDs<unknown> | undefined
  >(undefined);
  useEffect(() => {
    setTitle([]);
    selectFeature(undefined);
    setFeatureDetailsLoadingStatus('NOT_LOADED');

    if (!featuresWithIds || featuresWithIds.length === 0) {
      setFeatureDetailsLoadingStatus('CANNOT_BE_LOADED');
    }
  }, [featuresWithIds]);
  const featureLayerFromWebMap =
    useFeatureLayerFromWebMapForFeatures(featuresWithIds);
  useEffect(() => {
    if (featureLayerFromWebMap.type === 'NOT_AVAILABLE') {
      setFeatureDetailsLoadingStatus('CANNOT_BE_LOADED');
      return;
    }

    if (
      featureLayerFromWebMap.type !== 'LOADED' ||
      featureWithId ||
      !featuresWithIds ||
      !featureLayerFromWebMap ||
      featureDetailsLoadingStatus === 'LOADING'
    )
      return;

    const gridElements = featuresWithIds.map(m => ({
      globalId: m.gc.globalId,
      objectId: m.id as number,
      geometry: geojsonToArcGIS(m) as Graphic
    }));

    setFeatureDetailsLoadingStatus('LOADING');

    const titles = gridElements.map(gridElement => {
      const graphic = new Graphic({
        attributes: gridElement.geometry.attributes,
        geometry: gridElement.geometry.geometry,
        layer: featureLayerFromWebMap.layer[gridElement.globalId]
      });

      const feature = new Feature({
        graphic,
        visibleElements: {
          title: false
        }
      });

      return reactiveUtils
        .once(() => feature.title)
        .then(title => ({ globalId: gridElement.globalId, title }));
    });

    Promise.allSettled(titles)
      .then(results => {
        return results.filter(r => r.status === 'fulfilled').map(r => r.value);
      })
      .then(gridElementTitles => {
        setTitle(gridElementTitles);
        setFeatureDetailsLoadingStatus('LOADED');
      });

    selectFeature(featuresWithIds[0]);
  }, [featureLayerFromWebMap]);

  return { status: featureDetailsLoadingStatus, titles };
}

export function useTitlesResultsOfGlobalIds({
  of: globalIds
}: {
  of: DomainGuid[];
}) {
  const features = useAppState().arcgis.featureGeometries;
  const [featuresWithIds, setFeaturesWithIds] = React.useState<
    FeatureWithIDs<unknown>[] | undefined
  >([]);

  useEffect(() => {
    if (features.current === 'LOADED') {
      const featuresWithIds = features.value.flatMap(featuresInLayer => {
        const f = featuresInLayer.features.filter(feature =>
          globalIds.includes(feature.gc.globalId)
        );
        return f;
      });
      setFeaturesWithIds(featuresWithIds);
    }
  }, [features.current]);

  return useTitlesOfFeatures({ of: featuresWithIds });
}

export function useTitleOfSelectedElement() {
  const feature = useAppState().selectedFeature.current;
  const [featuresWithIds, setFeaturesWithIds] = React.useState<
    FeatureWithIDs<unknown>[] | undefined
  >(feature ? [feature] : undefined);

  useEffect(() => {
    setFeaturesWithIds(featuresWithIds);
  }, [feature]);

  return useTitlesOfFeatures({ of: featuresWithIds });
}

export declare module NearbyObjectsWithRelatedLayers2 {
  type ByGlobalId = { globalIds: DomainGuid[]; layer: AnyLayer }[];
}

export function queryGridElementsDetailsByGlobalID2(
  objects: NearbyObjectsWithRelatedLayers2.ByGlobalId
) {
  return Promise.allSettled(_queryGridElementsDetailsByGlobalID2(objects)).then(
    takeOnlyCompleted
  );

  function _queryGridElementsDetailsByGlobalID2(
    objects: NearbyObjectsWithRelatedLayers2.ByGlobalId
  ) {
    const queryableLayers = takeQueryableLayers2(objects);
    const gridElementDetails = queryableLayers.map(result => {
      const where = `globalid in (\'${result.globalIds.join("','")}\')`;
      return asFeatureLayer(result.layer).queryFeatures({
        where,
        spatialRelationship: 'intersects',
        returnGeometry: true,
        outFields: ['*']
      });
    });
    return gridElementDetails;

    function asFeatureLayer(layer: QueryableLayer) {
      const featureLayer = layer as unknown as __esri.FeatureLayer;
      return featureLayer;
    }
  }
}

type TakeQueryableLayers2 = (
  layers: { layer: AnyLayer }[]
) => { layer: QueryableLayer; objectid: number; globalIds: DomainGuid[] }[];

type LayerWithFeatureIdentifiers2 = ReturnType<TakeQueryableLayers2>[number];

const takeQueryableLayers2: TakeQueryableLayers2 = response => {
  return response
    .filter(object => object.layer)
    .filter(
      object =>
        object.layer.type === 'feature' ||
        object.layer.type === 'subtype-sublayer' ||
        object.layer.type === 'subtype-group'
    )
    .map(object => object as unknown as LayerWithFeatureIdentifiers2);
};
